import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../App";
import { useCurrency } from "../contexts/CurrencyContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { toast } from "sonner";
import { Plus, Search, Pencil, Trash2, FileSignature } from "lucide-react";

var API_URL = process.env.REACT_APP_BACKEND_URL;

var contractTypes = ["lease", "sale", "service", "management"];
var contractStatuses = ["draft", "active", "expired", "terminated"];

var emptyForm = {
  name: "",
  contract_type: "lease",
  property_id: "",
  unit_id: "",
  party_name: "",
  party_email: "",
  party_phone: "",
  start_date: "",
  end_date: "",
  value: "",
  status: "active",
  terms: "",
};

export default function Contracts() {
  var { user, token } = useAuth();
  var { t } = useTranslation();
  var { formatPrice } = useCurrency();
  var [contracts, setContracts] = useState([]);
  var [properties, setProperties] = useState([]);
  var [units, setUnits] = useState([]);
  var [filterUnits, setFilterUnits] = useState([]);
  var [loading, setLoading] = useState(true);
  var [dialogOpen, setDialogOpen] = useState(false);
  var [editingContract, setEditingContract] = useState(null);
  var [form, setForm] = useState(emptyForm);
  var [search, setSearch] = useState("");
  var [filterProperty, setFilterProperty] = useState("all");
  var [filterUnit, setFilterUnit] = useState("all");

  var canEdit = user?.role === "admin" || user?.role === "realestate_admin" || user?.role === "realestate_manager";
  var isAdmin = user?.role === "admin";

  useEffect(function() { fetchData(); }, []);

  // Fetch units when form property changes (for dialog)
  useEffect(function() {
    if (form.property_id && form.property_id !== "none") {
      fetchUnits(form.property_id);
    } else {
      setUnits([]);
      setForm(function(f) { return { ...f, unit_id: "" }; });
    }
  }, [form.property_id]);

  // Fetch units when filter property changes
  useEffect(function() {
    if (filterProperty && filterProperty !== "all") {
      fetchFilterUnits(filterProperty);
    } else {
      setFilterUnits([]);
      setFilterUnit("all");
    }
  }, [filterProperty]);

  async function fetchData() {
    try {
      var contractsRes = await fetch(API_URL + "/api/contracts", {
        credentials: "include",
        headers: token ? { Authorization: "Bearer " + token } : {},
      });
      var propertiesRes = await fetch(API_URL + "/api/properties", {
        credentials: "include",
        headers: token ? { Authorization: "Bearer " + token } : {},
      });
      if (contractsRes.ok) setContracts(await contractsRes.json());
      if (propertiesRes.ok) setProperties(await propertiesRes.json());
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  }

  async function fetchUnits(propertyId) {
    try {
      var response = await fetch(API_URL + "/api/units?property_id=" + propertyId, {
        credentials: "include",
        headers: token ? { Authorization: "Bearer " + token } : {},
      });
      if (response.ok) {
        setUnits(await response.json());
      }
    } catch (error) {
      console.error("Error fetching units:", error);
    }
  }

  async function fetchFilterUnits(propertyId) {
    try {
      var response = await fetch(API_URL + "/api/units?property_id=" + propertyId, {
        credentials: "include",
        headers: token ? { Authorization: "Bearer " + token } : {},
      });
      if (response.ok) {
        setFilterUnits(await response.json());
      }
    } catch (error) {
      console.error("Error fetching filter units:", error);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    try {
      var url = editingContract ? API_URL + "/api/contracts/" + editingContract.contract_id : API_URL + "/api/contracts";
      var method = editingContract ? "PUT" : "POST";

      var payload = {
        name: form.name,
        contract_type: form.contract_type,
        property_id: form.property_id === "none" || !form.property_id ? null : form.property_id,
        unit_id: form.unit_id === "none" || !form.unit_id ? null : form.unit_id,
        party_name: form.party_name,
        party_email: form.party_email || null,
        party_phone: form.party_phone || null,
        start_date: form.start_date,
        end_date: form.end_date || null,
        value: form.value ? parseFloat(form.value) : null,
        status: form.status,
        terms: form.terms || null,
      };

      var response = await fetch(url, {
        method: method,
        headers: { "Content-Type": "application/json", Authorization: token ? "Bearer " + token : "" },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        toast.success(editingContract ? t("common.success") : t("common.success"));
        setDialogOpen(false);
        setEditingContract(null);
        setForm(emptyForm);
        fetchData();
      } else {
        var error = await response.json();
        toast.error(error.detail || t("common.error"));
      }
    } catch (error) {
      toast.error(t("common.error"));
    }
  }

  function handleEdit(contract) {
    setEditingContract(contract);
    setForm({
      name: contract.name || "",
      contract_type: contract.contract_type || "lease",
      property_id: contract.property_id || "",
      unit_id: contract.unit_id || "",
      party_name: contract.party_name || "",
      party_email: contract.party_email || "",
      party_phone: contract.party_phone || "",
      start_date: contract.start_date || "",
      end_date: contract.end_date || "",
      value: contract.value?.toString() || "",
      status: contract.status || "active",
      terms: contract.terms || "",
    });
    // Fetch units for the selected property
    if (contract.property_id) {
      fetchUnits(contract.property_id);
    }
    setDialogOpen(true);
  }

  async function handleDelete(contractId) {
    if (!window.confirm(t("common.confirmDelete"))) return;
    try {
      var response = await fetch(API_URL + "/api/contracts/" + contractId, {
        method: "DELETE",
        credentials: "include",
        headers: token ? { Authorization: "Bearer " + token } : {},
      });
      if (response.ok) {
        toast.success(t("common.success"));
        fetchData();
      } else {
        toast.error(t("common.error"));
      }
    } catch (error) {
      toast.error(t("common.error"));
    }
  }

  function getPropertyName(propertyId) {
    if (!propertyId) return "-";
    for (var i = 0; i < properties.length; i++) {
      if (properties[i].property_id === propertyId) return properties[i].name;
    }
    return "-";
  }

  function getUnitName(unitId) {
    if (!unitId) return "-";
    for (var i = 0; i < units.length; i++) {
      if (units[i].unit_id === unitId) return units[i].name;
    }
    return unitId.substring(0, 8) + "...";
  }

  function getStatusBadge(status) {
    var statusClasses = {
      draft: "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400",
      active: "status-active",
      expired: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400",
      terminated: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
    };
    return <Badge className={statusClasses[status] || ""}>{t("contracts.statuses." + status)}</Badge>;
  }

  function getFilteredContracts() {
    var result = [];
    var searchLower = search.toLowerCase();
    for (var i = 0; i < contracts.length; i++) {
      var c = contracts[i];
      var matchName = c.name && c.name.toLowerCase().indexOf(searchLower) >= 0;
      var matchParty = c.party_name && c.party_name.toLowerCase().indexOf(searchLower) >= 0;
      var matchSearch = matchName || matchParty;
      var matchProperty = filterProperty === "all" || c.property_id === filterProperty;
      var matchUnit = filterUnit === "all" || c.unit_id === filterUnit;
      if (matchSearch && matchProperty && matchUnit) result.push(c);
    }
    return result;
  }

  function renderFilterPropertyOptions() {
    var items = [<SelectItem key="all" value="all">{t("common.all")}</SelectItem>];
    for (var i = 0; i < properties.length; i++) {
      items.push(<SelectItem key={properties[i].property_id} value={properties[i].property_id}>{properties[i].name}</SelectItem>);
    }
    return items;
  }

  function renderFilterUnitOptions() {
    var items = [<SelectItem key="all" value="all">{t("common.all")}</SelectItem>];
    for (var i = 0; i < filterUnits.length; i++) {
      items.push(<SelectItem key={filterUnits[i].unit_id} value={filterUnits[i].unit_id}>{filterUnits[i].name}</SelectItem>);
    }
    return items;
  }

  function renderPropertyOptions() {
    var items = [<SelectItem key="none" value="none">{t("common.none")}</SelectItem>];
    for (var i = 0; i < properties.length; i++) {
      items.push(<SelectItem key={properties[i].property_id} value={properties[i].property_id}>{properties[i].name}</SelectItem>);
    }
    return items;
  }

  function renderUnitOptions() {
    var items = [<SelectItem key="none" value="none">{t("common.none")}</SelectItem>];
    for (var i = 0; i < units.length; i++) {
      items.push(<SelectItem key={units[i].unit_id} value={units[i].unit_id}>{units[i].name}</SelectItem>);
    }
    return items;
  }

  function renderContractTypeOptions() {
    var items = [];
    for (var i = 0; i < contractTypes.length; i++) {
      items.push(<SelectItem key={contractTypes[i]} value={contractTypes[i]}>{t("contracts.types." + contractTypes[i])}</SelectItem>);
    }
    return items;
  }

  function renderStatusOptions() {
    var items = [];
    for (var i = 0; i < contractStatuses.length; i++) {
      items.push(<SelectItem key={contractStatuses[i]} value={contractStatuses[i]}>{t("contracts.statuses." + contractStatuses[i])}</SelectItem>);
    }
    return items;
  }

  function renderContractRows() {
    var filteredContracts = getFilteredContracts();
    var rows = [];
    for (var i = 0; i < filteredContracts.length; i++) {
      var contract = filteredContracts[i];
      var contractId = contract.contract_id;
      var contractData = JSON.parse(JSON.stringify(contract));
      rows.push(
        <TableRow key={contractId} data-testid={"contract-row-" + contractId}>
          <TableCell className="font-medium">{contract.name}</TableCell>
          <TableCell><Badge variant="outline">{t("contracts.types." + contract.contract_type)}</Badge></TableCell>
          <TableCell>{getPropertyName(contract.property_id)}</TableCell>
          <TableCell>{contract.unit_id ? <Badge variant="secondary">{getUnitName(contract.unit_id)}</Badge> : "-"}</TableCell>
          <TableCell>{contract.party_name}</TableCell>
          <TableCell>{contract.start_date}</TableCell>
          <TableCell>{contract.end_date || "-"}</TableCell>
          <TableCell>{contract.value ? formatPrice(contract.value) : "-"}</TableCell>
          <TableCell>{getStatusBadge(contract.status)}</TableCell>
          {isAdmin && (
            <TableCell>
              {contract.owner_name ? (
                <div>
                  <div className="font-medium text-sm">{contract.owner_name}</div>
                  <div className="text-xs text-muted-foreground">{contract.owner_email}</div>
                </div>
              ) : "-"}
            </TableCell>
          )}
          {canEdit && (
            <TableCell>
              <div className="flex gap-1">
                <Button variant="ghost" size="icon" onClick={handleEdit.bind(null, contractData)}><Pencil className="w-4 h-4" /></Button>
                <Button variant="ghost" size="icon" onClick={handleDelete.bind(null, contractId)}><Trash2 className="w-4 h-4 text-destructive" /></Button>
              </div>
            </TableCell>
          )}
        </TableRow>
      );
    }
    return rows;
  }

  var filteredContracts = getFilteredContracts();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="contracts-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t("contracts.title")}</h1>
          <p className="text-muted-foreground mt-1">{t("contracts.subtitle")}</p>
        </div>
        {canEdit && (
          <Dialog open={dialogOpen} onOpenChange={function(open) { setDialogOpen(open); if (!open) { setEditingContract(null); setForm(emptyForm); setUnits([]); } }}>
            <DialogTrigger asChild>
              <Button data-testid="add-contract-btn"><Plus className="w-4 h-4 mr-2" />{t("contracts.addContract")}</Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingContract ? t("contracts.editContract") : t("contracts.addNewContract")}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label className="form-label">{t("contracts.contractName")}</Label>
                  <Input value={form.name} onChange={function(e) { setForm({ ...form, name: e.target.value }); }} placeholder="e.g., Annual Lease Agreement" required data-testid="contract-name-input" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="form-label">{t("contracts.contractType")}</Label>
                    <Select value={form.contract_type} onValueChange={function(v) { setForm({ ...form, contract_type: v }); }}>
                      <SelectTrigger data-testid="contract-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{renderContractTypeOptions()}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="form-label">{t("common.status")}</Label>
                    <Select value={form.status} onValueChange={function(v) { setForm({ ...form, status: v }); }}>
                      <SelectTrigger data-testid="contract-status-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{renderStatusOptions()}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="form-label">{t("assets.property")}</Label>
                    <Select value={form.property_id || "none"} onValueChange={function(v) { setForm({ ...form, property_id: v, unit_id: "" }); }}>
                      <SelectTrigger data-testid="contract-property-select"><SelectValue placeholder={t("common.optional")} /></SelectTrigger>
                      <SelectContent>{renderPropertyOptions()}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="form-label">{t("common.unit")}</Label>
                    <Select value={form.unit_id || "none"} onValueChange={function(v) { setForm({ ...form, unit_id: v }); }} disabled={!form.property_id || form.property_id === "none" || units.length === 0}>
                      <SelectTrigger data-testid="contract-unit-select"><SelectValue placeholder={t("common.optional")} /></SelectTrigger>
                      <SelectContent>{renderUnitOptions()}</SelectContent>
                    </Select>
                    {form.property_id && form.property_id !== "none" && units.length === 0 && (
                      <p className="text-xs text-muted-foreground mt-1">{t("common.noUnitsAvailable")}</p>
                    )}
                  </div>
                </div>
                <div>
                  <Label className="form-label">{t("contracts.partyName")}</Label>
                  <Input value={form.party_name} onChange={function(e) { setForm({ ...form, party_name: e.target.value }); }} placeholder="Contracting party name" required data-testid="contract-party-name-input" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="form-label">{t("contracts.partyEmail")}</Label>
                    <Input type="email" value={form.party_email} onChange={function(e) { setForm({ ...form, party_email: e.target.value }); }} placeholder="email@example.com" data-testid="contract-party-email-input" />
                  </div>
                  <div>
                    <Label className="form-label">{t("contracts.partyPhone")}</Label>
                    <Input value={form.party_phone} onChange={function(e) { setForm({ ...form, party_phone: e.target.value }); }} placeholder="+1..." data-testid="contract-party-phone-input" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="form-label">{t("contracts.startDate")}</Label>
                    <Input type="date" value={form.start_date} onChange={function(e) { setForm({ ...form, start_date: e.target.value }); }} required data-testid="contract-start-date-input" />
                  </div>
                  <div>
                    <Label className="form-label">{t("contracts.endDate")}</Label>
                    <Input type="date" value={form.end_date} onChange={function(e) { setForm({ ...form, end_date: e.target.value }); }} data-testid="contract-end-date-input" />
                  </div>
                </div>
                <div>
                  <Label className="form-label">{t("contracts.value")}</Label>
                  <Input type="number" value={form.value} onChange={function(e) { setForm({ ...form, value: e.target.value }); }} placeholder="0" data-testid="contract-value-input" />
                </div>
                <div>
                  <Label className="form-label">{t("contracts.terms")}</Label>
                  <Textarea value={form.terms} onChange={function(e) { setForm({ ...form, terms: e.target.value }); }} placeholder="Contract terms..." rows={3} data-testid="contract-terms-input" />
                </div>
                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={function() { setDialogOpen(false); }}>{t("common.cancel")}</Button>
                  <Button type="submit" data-testid="contract-submit-btn">{editingContract ? t("common.update") : t("common.create")}</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input placeholder={t("common.search")} value={search} onChange={function(e) { setSearch(e.target.value); }} className="pl-10" data-testid="contract-search-input" />
        </div>
        <Select value={filterProperty} onValueChange={function(v) { setFilterProperty(v); setFilterUnit("all"); }}>
          <SelectTrigger className="w-48" data-testid="contract-filter-property"><SelectValue placeholder={t("assets.property")} /></SelectTrigger>
          <SelectContent>{renderFilterPropertyOptions()}</SelectContent>
        </Select>
        <Select value={filterUnit} onValueChange={setFilterUnit} disabled={filterProperty === "all" || filterUnits.length === 0}>
          <SelectTrigger className="w-48" data-testid="contract-filter-unit"><SelectValue placeholder={t("common.unit")} /></SelectTrigger>
          <SelectContent>{renderFilterUnitOptions()}</SelectContent>
        </Select>
      </div>

      {filteredContracts.length > 0 ? (
        <Card className="bento-card overflow-hidden">
          <Table className="data-table">
            <TableHeader>
              <TableRow>
                <TableHead>{t("common.name")}</TableHead>
                <TableHead>{t("common.type")}</TableHead>
                <TableHead>{t("assets.property")}</TableHead>
                <TableHead>{t("common.unit")}</TableHead>
                <TableHead>{t("contracts.partyName")}</TableHead>
                <TableHead>{t("contracts.startDate")}</TableHead>
                <TableHead>{t("contracts.endDate")}</TableHead>
                <TableHead>{t("contracts.value")}</TableHead>
                <TableHead>{t("common.status")}</TableHead>
                {isAdmin && <TableHead>{t("common.owner")}</TableHead>}
                {canEdit && <TableHead className="w-20">{t("common.actions")}</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>{renderContractRows()}</TableBody>
          </Table>
        </Card>
      ) : (
        <Card className="bento-card">
          <CardContent className="py-12 text-center text-muted-foreground">
            <FileSignature className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>{t("contracts.noContracts")}</p>
            {canEdit && <Button className="mt-4" onClick={function() { setDialogOpen(true); }}>{t("contracts.addFirstContract")}</Button>}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
